import AccountsAPI from '@infra/api/accounts';
import AddressesAPI from '@infra/api/addresses';
import AdministrationAPI from '@infra/api/administration';
import AssistedImportAPI from '@infra/api/assisted-import';
import AuthAPI from '@infra/api/auth';
import CartAPI from '@infra/api/cart';
import CatalogAPI from '@infra/api/catalog';
import CatalogArtworkAPI from '@infra/api/catalog-artwork';
import CheckoutAPI from '@infra/api/checkout';
import CollectionTranslationsAPI from '@infra/api/collection-translations';
import CouponsAPI from '@infra/api/coupons';
import DecksAPI from '@infra/api/decks';
import FinanceAPI from '@infra/api/finance';
import InventoryAPI from '@infra/api/inventory';
import InventoryBulkPriceAPI from '@infra/api/inventory-bulk-price';
import InventoryImportAPI from '@infra/api/inventory-import';
import LocalAPI from '@infra/api/local';
import OrdersAPI from '@infra/api/orders';
import PaymentSettingsAPI from '@infra/api/payment-settings';
import PlatformFinanceAPI from '@infra/api/platform-finance';
import ProductsAPI from '@infra/api/products';
import PromotionsAPI from '@infra/api/promotions';
import ShippingSettingsAPI from '@infra/api/shipping-settings';
import SlugBlacklistAPI from '@infra/api/slug-blacklist';
import StoresAPI from '@infra/api/stores';
import ViaCEPRepository from '@infra/viacep';
import { PromotionsUseCase } from '@lib/interfaces/usecase/promotions.use-case';
import AccountsService from '@lib/services/accounts.service';
import AddressesService from '@lib/services/addresses.service';
import AdministrationService from '@lib/services/administration.service';
import AssistedImportService from '@lib/services/assisted-import.service';
import AuthService from '@lib/services/auth.service';
import CartService from '@lib/services/cart.service';
import CatalogArtworkService from '@lib/services/catalog-artwork.service';
import CatalogService from '@lib/services/catalog.service';
import CheckoutService from '@lib/services/checkout.service';
import CollectionTranslationsService from '@lib/services/collection-translations.service';
import CouponsService from '@lib/services/coupons.service';
import DecksService from '@lib/services/decks.service';
import FinanceService from '@lib/services/finance.service';
import InventoryBulkPriceService from '@lib/services/inventory-bulk-price.service';
import InventoryImportService from '@lib/services/inventory-import.service';
import InventoryService from '@lib/services/inventory.service';
import LocalService from '@lib/services/local.service';
import OrdersService from '@lib/services/orders.service';
import PaymentSettingsService from '@lib/services/payment-settings.service';
import PlatformFinanceService from '@lib/services/platform-finance.service';
import ProductsService from '@lib/services/products.service';
import PromotionsService from '@lib/services/promotions.service';
import ShippingSettingsService from '@lib/services/shipping-settings.service';
import SlugBlacklistService from '@lib/services/slug-blacklist.service';
import StoresService from '@lib/services/stores.service';

export type ServiceKeys = 'auth';

const authUseCase = new AuthService(new AuthAPI());

const accountsUseCase = new AccountsService(new AccountsAPI());
const administrationUseCase = new AdministrationService(
  new AdministrationAPI(),
);

const addressesUseCase = new AddressesService(
  new AddressesAPI(),
  new ViaCEPRepository(),
);

const catalogUseCase = new CatalogService(new CatalogAPI());
const catalogArtworkUseCase = new CatalogArtworkService(
  new CatalogArtworkAPI(),
);

const cartUseCase = new CartService(new CartAPI());

const checkoutUseCase = new CheckoutService(new CheckoutAPI());

const decksUseCase = new DecksService(new DecksAPI());

const inventoryUseCase = new InventoryService(new InventoryAPI());
const inventoryImportUseCase = new InventoryImportService(
  new InventoryImportAPI(),
);
const inventoryBulkPriceUseCase = new InventoryBulkPriceService(
  new InventoryBulkPriceAPI(),
);
const assistedImportUseCase = new AssistedImportService(
  new AssistedImportAPI(),
);

const productsUseCase = new ProductsService(new ProductsAPI());
const promotionsUseCase = new PromotionsService(new PromotionsAPI());

const platformFinanceUseCase = new PlatformFinanceService(
  new PlatformFinanceAPI(),
);
const financeUseCase = new FinanceService(new FinanceAPI());

const ordersUseCase = new OrdersService(new OrdersAPI());

const paymentSettingsUseCase = new PaymentSettingsService(
  new PaymentSettingsAPI(),
);
const storesUseCase = new StoresService(new StoresAPI());
const shippingSettingsUseCase = new ShippingSettingsService(
  new ShippingSettingsAPI(),
);

const localUseCase = new LocalService(new LocalAPI());

const slugBlacklistUseCase = new SlugBlacklistService(new SlugBlacklistAPI());
const collectionTranslationsUseCase = new CollectionTranslationsService(
  new CollectionTranslationsAPI(),
);

const couponsUseCase = new CouponsService(new CouponsAPI());

const DIContainer = {
  getCouponsUseCase: () => couponsUseCase,
  getLocalUseCase: () => localUseCase,
  getAuthUseCase: () => authUseCase,
  getAccountsUseCase: () => accountsUseCase,
  getAdministrationUseCase: () => administrationUseCase,
  getAddressesUseCase: () => addressesUseCase,
  getCatalogUseCase: () => catalogUseCase,
  getCatalogArtworkUseCase: () => catalogArtworkUseCase,
  getCartUseCase: () => cartUseCase,
  getCheckoutUseCase: () => checkoutUseCase,
  getDecksUseCase: () => decksUseCase,
  getInventoryUseCase: () => inventoryUseCase,
  getInventoryImportUseCase: () => inventoryImportUseCase,
  getInventoryBulkPriceUseCase: () => inventoryBulkPriceUseCase,
  getAssistedImportUseCase: () => assistedImportUseCase,
  getProductsUseCase: () => productsUseCase,
  getPromotionsUseCase: (): PromotionsUseCase => promotionsUseCase,
  getPlatformFinanceUseCase: () => platformFinanceUseCase,
  getFinanceUseCase: () => financeUseCase,
  getOrdersUseCase: () => ordersUseCase,
  getPaymentSettingsUseCase: () => paymentSettingsUseCase,
  getSlugBlacklistUseCase: () => slugBlacklistUseCase,
  getCollectionTranslationsUseCase: () => collectionTranslationsUseCase,
  getStoresUseCase: () => storesUseCase,
  getShippingSettingsUseCase: () => shippingSettingsUseCase,
} as const;

export default DIContainer;
